import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Link } from 'foundations-components/transitional/components/Link';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Button } from 'foundations-components/transitional/components/Button';
import { Box } from 'foundations-components/transitional/components/Box';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { DownCaratIcon, LeftIcon, RemoveIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { usePublicCatalogFilters } from '../../publicCatalogQueries';
import usageTracker from '../../../../utils/usageTracker';
import MenuContent from './MenuContent';
import { useGetMenuStateContextWithValue, useMenuStateContext } from './mobileCategoriesMenuStateContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const MENU_TRANSITION_ANIMATION_MS = 300;
const MenuModal = styled.div.withConfig({
  displayName: "PublicCatalogMobileCategoriesMenu__MenuModal",
  componentId: "sc-3hwomf-0"
})(({
  isMenuOpen,
  theme: {
    color
  }
}) => ({
  overflow: 'auto',
  backgroundColor: color['fill-surface-default'],
  color: color['fill-accent-neutral-pressed'],
  height: '100vh',
  width: '100vw',
  position: 'fixed',
  textAlign: 'left',
  top: 0,
  left: 0,
  transition: `transform ${MENU_TRANSITION_ANIMATION_MS}ms ease`,
  transform: isMenuOpen ? 'translateY(0)' : 'translateY(100vh)',
  zIndex: 99
}));
const PublicCatalogMobileCategoriesMenu = ({
  buttonText = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyPublic.catalogPage.filters.browseCatalog"
  }),
  buttonUse = 'link'
}) => {
  const {
    filtersData,
    filtersError,
    filtersLoading
  } = usePublicCatalogFilters();
  const {
    openMenuModal,
    closeMenuModal,
    closeSubMenu,
    menuModalRef,
    isMenuModalRendered,
    isMenuOpen,
    isSubMenuOpen,
    activeSubMenu
  } = useMenuStateContext();
  const handleOpenMenuClick = () => {
    openMenuModal();
    usageTracker.track('interaction', {
      action: 'open-mobile-catalog-categories-menu'
    });
  };
  const handleCloseMenuClick = () => {
    closeMenuModal();
    usageTracker.track('interaction', {
      action: 'close-mobile-catalog-categories-menu'
    });
  };
  const handleBackButtonClick = () => {
    closeSubMenu();
    usageTracker.track('interaction', {
      action: 'mobile-catalog-categories-back-button-click'
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [buttonUse === 'link' && /*#__PURE__*/_jsx(Link, {
      onClick: handleOpenMenuClick,
      paddingBlock: "300",
      paddingInline: "400",
      use: "on-dark",
      children: buttonText
    }), buttonUse === 'dropdown' && /*#__PURE__*/_jsxs(Link, {
      display: "inline-block",
      onClick: handleOpenMenuClick,
      children: [buttonText, /*#__PURE__*/_jsx(IconHolder, {
        color: "icon-core-default",
        icon: DownCaratIcon,
        size: "100",
        verticalAlign: 'middle'
      })]
    }), isMenuModalRendered &&
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(MenuModal, {
      "aria-hidden": !isMenuOpen,
      "aria-modal": "true",
      "data-test-id": "mobile-catalog-categories-menu",
      isMenuOpen: isMenuOpen,
      ref: menuModalRef,
      role: "dialog",
      children: [/*#__PURE__*/_jsx(View, {
        inset: "0",
        position: "sticky",
        zIndex: 1,
        children: /*#__PURE__*/_jsx(Paragraph, {
          color: "text-primary-default",
          margin: "0",
          typography: "heading-100",
          children: /*#__PURE__*/_jsxs(TFlex, {
            align: "center",
            backgroundColor: "do-not-use-obsidian",
            justify: "between",
            paddingBlock: "200",
            paddingInline: "100",
            children: [/*#__PURE__*/_jsx(Box, {
              children: isSubMenuOpen && /*#__PURE__*/_jsxs(Button, {
                color: "text-interactive-on-fill",
                onClick: handleBackButtonClick,
                paddingBlock: "200",
                paddingInline: "300",
                use: "unstyled",
                children: [/*#__PURE__*/_jsx(LeftIcon, {
                  color: "icon-core-on-fill-default",
                  size: "500"
                }), /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "academyPublic.catalogPage.filters.backButton"
                })]
              })
            }), /*#__PURE__*/_jsx(Box, {
              grow: 0,
              children: /*#__PURE__*/_jsx(Button, {
                "aria-label": I18n.text('academyPublic.catalogPage.filters.closeMobileMenuButton'),
                onClick: handleCloseMenuClick,
                padding: "200",
                use: "link-on-dark",
                children: /*#__PURE__*/_jsx(RemoveIcon, {
                  color: "icon-core-on-fill-default",
                  size: "500"
                })
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsx(MenuContent, {
        activeSubMenu: activeSubMenu,
        filtersData: filtersData,
        filtersError: filtersError,
        filtersLoading: filtersLoading,
        isSubMenuOpen: isSubMenuOpen
      })]
    })]
  });
};
const PublicCatalogMobileCategoriesMenuWithContext = ({
  buttonUse,
  buttonText,
  subMenuName
}) => {
  const [MenuStateContext, menuStateContextValue] = useGetMenuStateContextWithValue(subMenuName);
  return /*#__PURE__*/_jsx(MenuStateContext.Provider, {
    value: menuStateContextValue,
    children: /*#__PURE__*/_jsx(PublicCatalogMobileCategoriesMenu, {
      buttonText: buttonText,
      buttonUse: buttonUse
    })
  });
};
export default PublicCatalogMobileCategoriesMenuWithContext;